import React, { useState, useEffect } from 'react';
import { Tag, Plus, Clock, Calendar, Check, X, Trash2, Edit2, Sparkles, Percent, DollarSign, Power } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getPromotions,
  createPromotion,
  updatePromotion,
  togglePromotion,
  deletePromotion,
  Promotion,
} from '../../services/PromotionService';
import { api } from '../../utils/api';
import { Button } from '../ui';

const DAYS = [
  { id: 1, label: 'Lun' },
  { id: 2, label: 'Mar' },
  { id: 3, label: 'Mié' },
  { id: 4, label: 'Jue' },
  { id: 5, label: 'Vie' },
  { id: 6, label: 'Sáb' },
  { id: 7, label: 'Dom' },
];

export function PromotionsManager() {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Formulario
  const [name, setName] = useState('');
  const [type, setType] = useState<'HAPPY_HOUR' | 'PERCENTAGE_DISCOUNT' | 'FIXED_DISCOUNT'>('HAPPY_HOUR');
  const [discountPct, setDiscountPct] = useState<number>(15);
  const [discountAmt, setDiscountAmt] = useState<number>(0);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1, 2, 3, 4, 5]);
  const [startTime, setStartTime] = useState('16:00');
  const [endTime, setEndTime] = useState('19:00');
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);

  const fetchPromos = async () => {
    try {
      setLoading(true);
      const data = await getPromotions();
      setPromotions(data);
    } catch (err) {
      console.warn('Error loading promotions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data?.data || res.data || []);
    } catch (err) {
      console.warn('Error loading categories:', err);
    }
  };

  useEffect(() => {
    fetchPromos();
    fetchCategories();
  }, []);

  const handleOpenCreate = () => {
    setEditingId(null);
    setName('');
    setType('HAPPY_HOUR');
    setDiscountPct(15);
    setDiscountAmt(0);
    setDaysOfWeek([1, 2, 3, 4, 5]);
    setStartTime('16:00');
    setEndTime('19:00');
    setCategoryId('');
    setShowModal(true);
  };

  const handleOpenEdit = (p: Promotion) => {
    setEditingId(p.id);
    setName(p.name);
    setType(p.type);
    setDiscountPct(p.discount_percentage);
    setDiscountAmt(p.discount_amount);
    setDaysOfWeek(p.days_of_week || []);
    setStartTime(p.start_time?.slice(0, 5) || '16:00');
    setEndTime(p.end_time?.slice(0, 5) || '19:00');
    setCategoryId(p.category_id || '');
    setShowModal(true);
  };

  const handleToggleDay = (dayId: number) => {
    setDaysOfWeek((prev) =>
      prev.includes(dayId) ? prev.filter((d) => d !== dayId) : [...prev, dayId].sort()
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('El nombre de la promoción es obligatorio');
      return;
    }
    setSaving(true);
    try {
      const payload: Partial<Promotion> = {
        name,
        type,
        discount_percentage: Number(discountPct),
        discount_amount: Number(discountAmt),
        days_of_week: daysOfWeek.length > 0 ? daysOfWeek : null,
        start_time: startTime || null,
        end_time: endTime || null,
        category_id: categoryId ? Number(categoryId) : null,
      };

      if (editingId) {
        await updatePromotion(editingId, payload);
        toast.success('Promoción actualizada con éxito');
      } else {
        await createPromotion(payload);
        toast.success('Promoción creada con éxito');
      }
      setShowModal(false);
      fetchPromos();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Error al guardar la promoción');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (p: Promotion) => {
    try {
      const res = await togglePromotion(p.id);
      toast.success(res.message);
      fetchPromos();
    } catch (err) {
      toast.error('Error al cambiar estado de la promoción');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Seguro que deseas eliminar esta promoción?')) return;
    try {
      await deletePromotion(id);
      toast.success('Promoción eliminada');
      fetchPromos();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-3xl border border-primary/20 bg-gradient-to-r from-amber-500/10 via-primary/5 to-transparent p-5 sm:p-6 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded-lg bg-primary/20 p-1.5 text-primary">
              <Sparkles className="h-4 w-4" />
            </span>
            <h2 className="text-base font-bold text-text">Promociones Automáticas y Happy Hour</h2>
          </div>
          <p className="text-xs text-text-muted mt-1 max-w-xl">
            Configura descuentos por horario programado (ej. 20% de descuento en cócteles o helados de 4:00 PM a 7:00 PM) para impulsar las ventas en momentos estratégicos.
          </p>
        </div>

        <Button
          variant="primary"
          size="md"
          onClick={handleOpenCreate}
          className="rounded-2xl flex items-center gap-2 shadow-md hover:scale-[1.02] transition-transform"
        >
          <Plus className="h-4 w-4" />
          Nueva Promoción
        </Button>
      </div>

      {/* ── Listado de Promociones ── */}
      {loading ? (
        <div className="py-12 text-center text-text-muted text-xs">Cargando promociones...</div>
      ) : promotions.length === 0 ? (
        <div className="rounded-3xl border border-border bg-surface p-12 text-center shadow-sm">
          <Tag className="mx-auto mb-3 h-10 w-10 text-text-muted" />
          <p className="text-base font-semibold text-text">No hay promociones configuradas</p>
          <p className="text-xs text-text-muted mt-1">
            Crea tu primer Happy Hour o descuento automático para atraer más clientes.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {promotions.map((p) => {
            const dayLabels = (p.days_of_week || [])
              .map((d) => DAYS.find((item) => item.id === d)?.label)
              .filter(Boolean)
              .join(', ');

            return (
              <div
                key={p.id}
                className={`rounded-3xl border p-5 shadow-sm transition-all flex flex-col justify-between ${
                  p.is_active
                    ? 'border-border bg-surface hover:border-primary/40'
                    : 'border-border/60 bg-surface/50 opacity-60'
                }`}
              >
                <div>
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-text text-base">{p.name}</h3>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                            p.is_active ? 'bg-emerald-500/15 text-emerald-600' : 'bg-muted text-text-muted'
                          }`}
                        >
                          {p.is_active ? 'Activa' : 'Pausada'}
                        </span>
                      </div>
                      <p className="text-xs text-primary font-bold mt-0.5">
                        {p.type === 'HAPPY_HOUR' && `⚡ Happy Hour: -${p.discount_percentage}%`}
                        {p.type === 'PERCENTAGE_DISCOUNT' && `Descuento: -${p.discount_percentage}%`}
                        {p.type === 'FIXED_DISCOUNT' && `Descuento Fijo: -$${p.discount_amount.toLocaleString()}`}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleToggleActive(p)}
                      title={p.is_active ? 'Pausar promoción' : 'Activar promoción'}
                      className={`h-8 w-8 rounded-xl flex items-center justify-center transition-colors ${
                        p.is_active
                          ? 'bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20'
                          : 'bg-muted text-text-muted hover:text-text'
                      }`}
                    >
                      <Power className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Detalles de horario y días */}
                  <div className="space-y-1.5 text-xs text-text-muted mb-4">
                    {dayLabels && (
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 text-primary" />
                        <span>Días: <strong className="text-text">{dayLabels}</strong></span>
                      </div>
                    )}
                    {p.start_time && p.end_time && (
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-amber-500" />
                        <span>Horario: <strong className="text-text">{p.start_time.slice(0, 5)} - {p.end_time.slice(0, 5)}</strong></span>
                      </div>
                    )}
                    {p.category && (
                      <div className="flex items-center gap-1.5">
                        <Tag className="h-3.5 w-3.5 text-indigo-500" />
                        <span>Aplica a categoría: <strong className="text-text">{p.category.name}</strong></span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-3 border-t border-border/70">
                  <button
                    type="button"
                    onClick={() => handleOpenEdit(p)}
                    className="p-1.5 rounded-lg text-text-muted hover:text-text hover:bg-surface-muted transition"
                    title="Editar"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(p.id)}
                    className="p-1.5 rounded-lg text-text-muted hover:text-rose-500 hover:bg-rose-500/10 transition"
                    title="Eliminar"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modal de Creación / Edición ── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-border bg-surface p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4 border-b border-border pb-3">
              <div className="flex items-center gap-2">
                <span className="rounded-lg bg-primary/10 p-2 text-primary">
                  <Tag className="h-5 w-5" />
                </span>
                <h3 className="text-base font-bold text-text">
                  {editingId ? 'Editar Promoción' : 'Nueva Promoción / Happy Hour'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-text-muted hover:text-text"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-text mb-1">Nombre de la Promoción</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Happy Hour Cócteles & Cervezas"
                  className="w-full rounded-2xl border border-border bg-surface px-3.5 py-2 text-sm text-text focus:border-primary focus:outline-none"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-text mb-1">Tipo de Promoción</label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value as any)}
                    className="w-full rounded-2xl border border-border bg-surface px-3 py-2 text-xs text-text focus:border-primary focus:outline-none"
                  >
                    <option value="HAPPY_HOUR">⚡ Happy Hour</option>
                    <option value="PERCENTAGE_DISCOUNT">Porcentaje (%)</option>
                    <option value="FIXED_DISCOUNT">Monto Fijo ($)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-text mb-1">
                    {type === 'FIXED_DISCOUNT' ? 'Monto Descuento ($)' : 'Porcentaje (%)'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={type === 'FIXED_DISCOUNT' ? 999999 : 100}
                    value={type === 'FIXED_DISCOUNT' ? discountAmt : discountPct}
                    onChange={(e) => {
                      if (type === 'FIXED_DISCOUNT') setDiscountAmt(Number(e.target.value));
                      else setDiscountPct(Number(e.target.value));
                    }}
                    className="w-full rounded-2xl border border-border bg-surface px-3.5 py-2 text-xs text-text font-bold focus:border-primary focus:outline-none"
                    required
                  />
                </div>
              </div>

              {/* Días de la semana */}
              <div>
                <label className="block text-xs font-semibold text-text mb-1.5">Días Válidos</label>
                <div className="flex gap-1.5 flex-wrap">
                  {DAYS.map((d) => {
                    const active = daysOfWeek.includes(d.id);
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => handleToggleDay(d.id)}
                        className={`rounded-xl px-3 py-1.5 text-xs font-bold transition-all ${
                          active
                            ? 'bg-primary text-white shadow-sm'
                            : 'bg-surface-muted text-text-muted border border-border hover:border-primary/40'
                        }`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Franja horaria */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-text mb-1">Hora Inicio</label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full rounded-2xl border border-border bg-surface px-3 py-1.5 text-xs text-text focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-text mb-1">Hora Fin</label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full rounded-2xl border border-border bg-surface px-3 py-1.5 text-xs text-text focus:border-primary focus:outline-none"
                  />
                </div>
              </div>

              {/* Categoría asociada (opcional) */}
              <div>
                <label className="block text-xs font-semibold text-text mb-1">Aplica a Categoría (Opcional)</label>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full rounded-2xl border border-border bg-surface px-3 py-2 text-xs text-text focus:border-primary focus:outline-none"
                >
                  <option value="">Todas las categorías</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                  className="rounded-2xl"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  disabled={saving}
                  className="rounded-2xl font-bold"
                >
                  {saving ? 'Guardando...' : editingId ? 'Guardar Cambios' : 'Crear Promoción'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
